/**
 * Helpers for displaying registrant/primary account info consistently.
 * For secondary accounts: company name and mailing address use the primary account;
 * the registering user's own name (and callers should use raw email/phone from the account)
 * identify who submitted the license. Primary users use their own fields throughout.
 */

/** Account-like object with optional own fields and optional primary_account_* fields (from API). */
export type RegistrantAccountDisplaySource = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  company_name?: string | null;
  name?: string | null;
  primary_account_address1?: string | null;
  primary_account_address2?: string | null;
  primary_account_city?: string | null;
  primary_account_state?: string | null;
  primary_account_zipcode?: string | null;
  primary_account_company_name?: string | null;
  primary_account_name?: string | null;
};

/** License list item with optional primary_record_* fields (when secondary user). */
export type PrimaryRecordDisplaySource = {
  primary_record_company_name?: string | null;
  primary_record_address1?: string | null;
  primary_record_address2?: string | null;
  primary_record_city?: string | null;
  primary_record_state?: string | null;
  primary_record_zipcode?: string | null;
};

// --- Registrant account (license details, account page) ---

export function getRegistrantDisplayAddress1(
  account: RegistrantAccountDisplaySource | null | undefined,
): string | null {
  if (!account) return null;
  const v = account.primary_account_address1 ?? account.address1 ?? null;
  return (v ?? "").trim() || null;
}

export function getRegistrantDisplayAddress2(
  account: RegistrantAccountDisplaySource | null | undefined,
): string | null {
  if (!account) return null;
  const v = account.primary_account_address2 ?? account.address2 ?? null;
  return (v ?? "").trim() || null;
}

export function getRegistrantDisplayCity(
  account: RegistrantAccountDisplaySource | null | undefined,
): string | null {
  if (!account) return null;
  const v = account.primary_account_city ?? account.city ?? null;
  return (v ?? "").trim() || null;
}

export function getRegistrantDisplayState(
  account: RegistrantAccountDisplaySource | null | undefined,
): string | null {
  if (!account) return null;
  const v = account.primary_account_state ?? account.state ?? null;
  return (v ?? "").trim() || null;
}

export function getRegistrantDisplayZipcode(
  account: RegistrantAccountDisplaySource | null | undefined,
): string | null {
  if (!account) return null;
  const v = account.primary_account_zipcode ?? account.zipcode ?? null;
  return (v ?? "").trim() || null;
}

export function getRegistrantDisplayCompanyName(
  account: RegistrantAccountDisplaySource | null | undefined,
): string | null {
  if (!account) return null;
  const v =
    account.primary_account_company_name ?? account.company_name ?? null;
  return (v ?? "").trim() || null;
}

export function getRegistrantDisplayName(
  account: RegistrantAccountDisplaySource | null | undefined,
): string | null {
  if (!account) return null;
  const v = account.name ?? account.primary_account_name ?? null;
  return (v ?? "").trim() || null;
}

/** True if any registrant display address field has a value. */
export function hasRegistrantDisplayAddress(
  account: RegistrantAccountDisplaySource | null | undefined,
): boolean {
  return !!(
    getRegistrantDisplayAddress1(account) ||
    getRegistrantDisplayAddress2(account) ||
    getRegistrantDisplayCity(account) ||
    getRegistrantDisplayState(account) ||
    getRegistrantDisplayZipcode(account)
  );
}

// --- License list (primary_record_* when secondary user) ---

export function getPrimaryRecordAddress1(
  source: PrimaryRecordDisplaySource | null | undefined,
): string | null {
  if (!source) return null;
  return (source.primary_record_address1 ?? "").trim() || null;
}

export function getPrimaryRecordAddress2(
  source: PrimaryRecordDisplaySource | null | undefined,
): string | null {
  if (!source) return null;
  return (source.primary_record_address2 ?? "").trim() || null;
}

export function getPrimaryRecordCity(
  source: PrimaryRecordDisplaySource | null | undefined,
): string | null {
  if (!source) return null;
  return (source.primary_record_city ?? "").trim() || null;
}

export function getPrimaryRecordState(
  source: PrimaryRecordDisplaySource | null | undefined,
): string | null {
  if (!source) return null;
  return (source.primary_record_state ?? "").trim() || null;
}

export function getPrimaryRecordZipcode(
  source: PrimaryRecordDisplaySource | null | undefined,
): string | null {
  if (!source) return null;
  return (source.primary_record_zipcode ?? "").trim() || null;
}

export function getPrimaryRecordCompanyName(
  source: PrimaryRecordDisplaySource | null | undefined,
): string | null {
  if (!source) return null;
  return (source.primary_record_company_name ?? "").trim() || null;
}

/** True if any primary record address field has a value (for license list). */
export function hasPrimaryRecordAddress(
  source: PrimaryRecordDisplaySource | null | undefined,
): boolean {
  return !!(
    getPrimaryRecordAddress1(source) ||
    getPrimaryRecordAddress2(source) ||
    getPrimaryRecordCity(source) ||
    getPrimaryRecordState(source) ||
    getPrimaryRecordZipcode(source)
  );
}

// --- Linked registrant (unregistered properties) ---

/**
 * Preferred display label for a linked registrant: the company name, falling
 * back to the personal name when the company name is blank.
 */
export function registrantDisplayName(account: {
  name?: string | null;
  company_name?: string | null;
}): string | null {
  return account.company_name || account.name || null;
}

/**
 * Label for registrant select options. Includes company, person name, and email
 * when present so the Select filter can match any of those fields.
 */
export function registrantSelectOptionLabel(account: {
  email?: string | null;
  name?: string | null;
  company_name?: string | null;
}): string {
  const company = account.company_name?.trim() || "";
  const name = account.name?.trim() || "";
  const email = account.email?.trim() || "";

  const identityParts: string[] = [];
  if (company) identityParts.push(company);
  if (name && name.toLowerCase() !== company.toLowerCase()) {
    identityParts.push(name);
  }
  const identity = identityParts.join(" · ");

  if (identity && email) {
    return `${identity} — ${email}`;
  }
  return identity || email || "—";
}

/** Shared option shape for every registrant-account picker. */
export function registrantSelectOption(account: {
  id: string;
  email?: string | null;
  name?: string | null;
  company_name?: string | null;
}): {
  value: string;
  label: string;
} {
  return {
    value: account.id,
    label: registrantSelectOptionLabel(account),
  };
}

/** Maps and alphabetizes registrant accounts consistently across portals. */
export function buildRegistrantSelectOptions(
  accounts: Array<{
    id: string;
    email?: string | null;
    name?: string | null;
    company_name?: string | null;
  }>,
): Array<{ value: string; label: string }> {
  return accounts.map(registrantSelectOption).sort((left, right) =>
    left.label.localeCompare(right.label, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

/**
 * Primary contacts own the organization. Secondary members have `primary_id`.
 * Pending invitees have `primary_id_pending` and no parent until they accept.
 */
export function isPrimaryRegistrantAccount(account: {
  primary_id?: string | null;
  primary_id_pending?: string | null;
}): boolean {
  return !account.primary_id && !account.primary_id_pending;
}

/** Label used in the operator and government registrant lists and detail sheet. */
export function organizationRoleLabel(account: {
  primary_id?: string | null;
  primary_id_pending?: string | null;
}): "Primary" | "Secondary" | "Pending" {
  if (account.primary_id) return "Secondary";
  if (account.primary_id_pending) return "Pending";
  return "Primary";
}
